import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

const TYPE_CONFIG = {
  salary:    { label: 'Salary',    color: '#2f9e44', bg: '#ebfbee', border: '#b2f2bb' },
  stipend:   { label: 'Stipend',   color: '#1971c2', bg: '#e7f5ff', border: '#a5d8ff' },
  incentive: { label: 'Incentive', color: '#6741d9', bg: '#f3f0ff', border: '#d0bfff' },
  bonus:     { label: 'Bonus',     color: '#e67700', bg: '#fff9db', border: '#ffec99' },
  deduction: { label: 'Deduction', color: '#c92a2a', bg: '#fff5f5', border: '#ffc9c9' },
};

function fmt(n) {
  return '₹' + Number(n).toLocaleString('en-IN');
}

export default function Wallet() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [adminWallet, setAdminWallet] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ type: 'salary', amount: '', description: '', month: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/wallet/my').then(r => setWallet(r.data)).catch(() => {});
    if (user?.role === 'superadmin') {
      api.get('/users').then(r => setUsers(r.data)).catch(() => {});
    }
  }, [user]);

  const loadUserWallet = async (uid) => {
    if (!uid) { setAdminWallet(null); return; }
    try {
      const { data } = await api.get('/wallet/' + uid);
      setAdminWallet(data);
    } catch (e) { toast.error('Failed to load wallet'); }
  };

  useEffect(() => { loadUserWallet(selectedUser); }, [selectedUser]);

  const addTransaction = async (e) => {
    e.preventDefault();
    if (!selectedUser) return toast.error('Select an employee');
    if (!form.amount || isNaN(form.amount)) return toast.error('Enter a valid amount');
    setSaving(true);
    try {
      const { data } = await api.post('/wallet/' + selectedUser + '/add', form);
      setAdminWallet(data);
      toast.success('Added to wallet');
      setShowAdd(false);
      setForm({ type: 'salary', amount: '', description: '', month: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteTransaction = async (txId) => {
    if (!window.confirm('Remove this transaction?')) return;
    try {
      const { data } = await api.delete('/wallet/' + selectedUser + '/transaction/' + txId);
      setAdminWallet(data);
      toast.success('Removed');
    } catch (e) { toast.error('Failed'); }
  };

  const currentMonth = new Date().toISOString().slice(0, 7);

  return (
    <div className="fadein">
      <div className="page-header">
        <div>
          <div className="page-title">Wallet</div>
          <div className="page-subtitle">Salary, stipend and incentive tracker</div>
        </div>
        {user?.role === 'superadmin' && selectedUser && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>+ Add Payment</button>
        )}
      </div>

      {/* Employee: my wallet */}
      {user?.role !== 'superadmin' && wallet && (
        <div>
          {/* Balance card */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
            <div className="stat-card" style={{ gridColumn: '1 / 2' }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Total Balance</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: wallet.balance >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(wallet.balance)}</div>
            </div>
            {/* This month summary */}
            {['salary', 'incentive', 'bonus'].map(type => {
              const total = wallet.transactions
                .filter(t => t.type === type && t.month === currentMonth)
                .reduce((s, t) => s + t.amount, 0);
              const cfg = TYPE_CONFIG[type];
              return (
                <div key={type} className="stat-card">
                  <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    {cfg.label} (This Month)
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: cfg.color }}>{fmt(total)}</div>
                </div>
              );
            })}
          </div>

          {/* Transaction history */}
          <div className="card">
            <div className="card-header">
              <span className="card-header-title">Transaction History</span>
            </div>
            {wallet.transactions.length === 0 ? (
              <div className="empty"><div className="empty-icon"></div><p>No transactions yet</p></div>
            ) : (
              <table>
                <thead>
                  <tr><th>Type</th><th>Amount</th><th>Description</th><th>Month</th><th>Date</th></tr>
                </thead>
                <tbody>
                  {[...wallet.transactions].reverse().map(tx => {
                    const cfg = TYPE_CONFIG[tx.type] || TYPE_CONFIG.bonus;
                    return (
                      <tr key={tx._id}>
                        <td>
                          <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                            {cfg.label}
                          </span>
                        </td>
                        <td style={{ fontWeight: 700, color: tx.type === 'deduction' ? 'var(--red)' : 'var(--green)', fontSize: 14 }}>
                          {tx.type === 'deduction' ? '-' : '+'}{fmt(tx.amount)}
                        </td>
                        <td style={{ color: 'var(--text-2)', fontSize: 13 }}>{tx.description || '—'}</td>
                        <td style={{ color: 'var(--text-3)', fontSize: 13 }}>{tx.month || '—'}</td>
                        <td style={{ color: 'var(--text-3)', fontSize: 12.5 }}>
                          {tx.createdAt ? format(new Date(tx.createdAt), 'MMM d, yyyy') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Superadmin: manage employee wallets */}
      {user?.role === 'superadmin' && (
        <div>
          {/* Employee selector */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
            <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)} style={{ minWidth: 240 }}>
              <option value="">Select employee to view wallet</option>
              {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.role})</option>)}
            </select>
          </div>

          {selectedUser && adminWallet && (
            <div>
              {/* Balance + summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
                <div className="stat-card">
                  <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Total Balance</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: adminWallet.balance >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(adminWallet.balance)}</div>
                </div>
                {Object.entries(TYPE_CONFIG).map(([type, cfg]) => {
                  const total = adminWallet.transactions
                    .filter(t => t.type === type)
                    .reduce((s, t) => s + t.amount, 0);
                  return (
                    <div key={type} className="stat-card">
                      <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{cfg.label}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: cfg.color }}>{fmt(total)}</div>
                    </div>
                  );
                })}
              </div>

              {/* Transactions */}
              <div className="card">
                <div className="card-header">
                  <span className="card-header-title">Transactions</span>
                  <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>+ Add Payment</button>
                </div>
                {adminWallet.transactions.length === 0 ? (
                  <div className="empty"><div className="empty-icon"></div><p>No transactions yet</p></div>
                ) : (
                  <table>
                    <thead>
                      <tr><th>Type</th><th>Amount</th><th>Description</th><th>Month</th><th>Added By</th><th>Date</th><th></th></tr>
                    </thead>
                    <tbody>
                      {[...adminWallet.transactions].reverse().map(tx => {
                        const cfg = TYPE_CONFIG[tx.type] || TYPE_CONFIG.bonus;
                        return (
                          <tr key={tx._id}>
                            <td>
                              <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                                {cfg.label}
                              </span>
                            </td>
                            <td style={{ fontWeight: 700, color: tx.type === 'deduction' ? 'var(--red)' : 'var(--green)', fontSize: 14 }}>
                              {tx.type === 'deduction' ? '-' : '+'}{fmt(tx.amount)}
                            </td>
                            <td style={{ color: 'var(--text-2)', fontSize: 13 }}>{tx.description || '—'}</td>
                            <td style={{ color: 'var(--text-3)', fontSize: 13 }}>{tx.month || '—'}</td>
                            <td style={{ fontSize: 13 }}>{tx.addedBy?.name || 'Admin'}</td>
                            <td style={{ color: 'var(--text-3)', fontSize: 12.5 }}>
                              {tx.createdAt ? format(new Date(tx.createdAt), 'MMM d, yyyy') : '—'}
                            </td>
                            <td>
                              <button className="btn btn-xs btn-danger" onClick={() => deleteTransaction(tx._id)}>Remove</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {!selectedUser && (
            <div className="card">
              <div className="empty"><div className="empty-icon"></div><p>Select an employee to view their wallet</p></div>
            </div>
          )}
        </div>
      )}

      {/* Add payment modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <h2>Add Payment</h2>
              <button className="modal-close" onClick={() => setShowAdd(false)}>✕</button>
            </div>
            <div className="modal-body">
              <form onSubmit={addTransaction}>
                <div className="form-group">
                  <label>Type</label>
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                    <option value="salary">Salary</option>
                    <option value="stipend">Stipend</option>
                    <option value="incentive">Incentive</option>
                    <option value="bonus">Bonus</option>
                    <option value="deduction">Deduction</option>
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Amount (₹)</label>
                    <input type="number" min="1" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="e.g. 15000" required />
                  </div>
                  <div className="form-group">
                    <label>Month</label>
                    <input type="month" value={form.month} onChange={e => setForm(f => ({ ...f, month: e.target.value }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. May 2024 salary, Performance bonus..." />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" type="submit" disabled={saving} style={{ flex: 1 }}>
                    {saving ? 'Adding...' : 'Add to Wallet'}
                  </button>
                  <button className="btn btn-secondary" type="button" onClick={() => setShowAdd(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
